/**
 * EditNominationPage — the nominating BD edits a submitted nomination.
 *
 * Route: `/event/nominations/:nominationId/edit`. Reached from "Edit" on the nomination's row in
 * the Nomination List and in the NominationDetail modal, offered only where
 * utils/nominationEdit `canEditNomination` holds (the creator, while the event is open).
 *
 * Only finds the nomination; the form is CreateNominationPage's, in edit mode — one form, so a
 * field added to nominating cannot be forgotten when editing (see its "Edit mode" note).
 *
 * Found through `ListNominations`, because the service has no GetNomination. That list is scoped to
 * the caller (own plus reports' for a BD), and an editor is the creator, so their own nomination is
 * always in it; one that is not is either gone or not theirs, and the page says "not found" for
 * both rather than guess.
 *
 * Gated like create: BD / admin (the route registry's `PATCH /nominations/{id}` is BD_AND_ADMIN). A
 * marketer gets the refusal without the list request going out.
 */

import { useParams } from 'react-router-dom';
import { Alert, Box, ContentLayout, Header } from '@amzn/awsui-components-react';
import { useEventHubRoles } from '../../hooks/useEventHubRoles';
import { useNominationList } from '../../hooks/useNominationList';
import { useEventHubLoading } from '../../components/EventHubLayout/EventHubLoading';
import { canNominate } from '../../utils/authorization';
import { NominationForm } from '../CreateNominationPage/CreateNominationPage';

export default function EditNominationPage() {
  const { nominationId } = useParams<{ nominationId: string }>();
  const mayEdit = canNominate(useEventHubRoles());
  const { nominations, loading, error } = useNominationList(mayEdit);
  useEventHubLoading(loading);

  const header = <Header variant="h1">Edit Nomination</Header>;

  if (!mayEdit) {
    return (
      <ContentLayout header={header}>
        <Alert type="error" header="You cannot edit nominations">
          Editing a nomination requires a BD role in Event Hub. Only the BD who submitted a
          nomination can edit it.
        </Alert>
      </ContentLayout>
    );
  }
  if (loading) {
    // The layout's overlay shows meanwhile.
    return <ContentLayout header={header}>{null}</ContentLayout>;
  }
  if (error) {
    return (
      <ContentLayout header={header}>
        <Alert type="error" header="Failed to load the nomination">
          {error}
        </Alert>
      </ContentLayout>
    );
  }

  const nomination = nominations.find((n) => n.nominationId === nominationId);
  if (!nomination) {
    return (
      <ContentLayout header={header}>
        <Alert type="error" header="Nomination not found">
          No nomination of yours matches <Box variant="code">{nominationId}</Box>.
        </Alert>
      </ContentLayout>
    );
  }

  /* Keyed by the nomination: the route stays mounted when only the id changes, and the draft
     must not carry one nomination's edits onto another. */
  return <NominationForm key={nomination.nominationId} editing={nomination} />;
}
